import { apiClient } from "rp-components"
import Vue from "vue"
import VueRouter from "vue-router"
import { RouteConfig } from "vue-router"

Vue.use(VueRouter)

const routes: RouteConfig[] = [
    {
        path: "/public",
        component: () => import("@/pages/BaseAuthenticated/BaseAuthenticated.vue"),
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) => {
            apiClient.events.addEventListener("unauthenticated", () => {
                if (router.currentRoute.path !== "/public/login") {
                    router.push("/public/login")
                }
            })
            next()
        },
        children: [
            {
                path: "",
                redirect: "properties",
                meta: { requiresAuth: true },
                name: "home"
            },
            {
                path: "properties",
                component: () => import("@/pages/Properties/Properties.vue"),
                meta: { requiresAuth: true },
                name: "properties"
            },
            {
                path: "support",
                component: () => import("@/pages/Support/Support.vue"),
                meta: { requiresAuth: true },
                name: "support"
            },
            {
                path: "properties/:propertyId",
                meta: { requiresAuth: true },
                component: () => import("@/pages/PropertyDetail/PropertyDetail.vue"),
                name: "propertyDetail",
                children: [
                    {
                        path: "spaces",
                        component: () => import("@/pages/SpacesList/SpacesList.vue"),
                        meta: { requiresAuth: true },
                        name: "spacesList",
                        children: [
                            {
                                path: ":spaceId",
                                meta: { requiresAuth: true },
                                component: () => import("@/pages/SpaceDetail/SpaceDetail.vue"),
                                name: "spaceDetail"
                            }
                        ]
                    },
                    {
                        path: "calendar",
                        meta: { requiresAuth: true },
                        component: () => import("@/pages/SpacesCalendar/SpacesCalendar.vue"),
                        name: "spacesCalendar"
                    },
                    {
                        path: "maintenance",
                        component: () => import("@/pages/MaintenanceTickets/MaintenanceTickets.vue"),
                        meta: { requiresAuth: true },
                        name: "maintenanceTickets",
                        redirect: { name: "openTickets" },
                        children: [
                            {
                                path: "open",
                                name: "openTickets"
                            },
                            {
                                path: "complete",
                                name: "completeTickets"
                            }
                        ]
                    },
                    {
                        path: "promotions",
                        component: () => import("@/pages/PromotionIndex/PromotionIndex.vue"),
                        name: "promotions",
                        meta: { requiresAuth: true },
                        children: [
                            {
                                path: ":id",
                                component: () => import("@/pages/PromotionList/PromotionList.vue"),
                                name: "promotionList",
                                meta: { requiresAuth: true }
                            }
                        ]
                    },
                    {
                        path: "building-access",
                        component: () => import("@/pages/BuildingAccess/BuildingAccess.vue"),
                        name: "buildingAccess",
                        meta: { requiresAuth: true }
                    },
                    {
                        path: "orders/:orderId",
                        component: () => import("@/pages/Order/Order.vue"),
                        name: "orders",
                        meta: { requiresAuth: true }
                    },
                    {
                        path: "bookable-amenities",
                        component: () => import("@/pages/BookableAmenities/BookableAmenities.vue"),
                        name: "bookableAmenities",
                        meta: { requiresAuth: true },
                        children: [
                            {
                                path: ":amenityId",
                                component: () => import("@/pages/AmenityDetail/AmenityDetail.vue"),
                                name: "amenityDetail",
                                meta: { requiresAuth: true }
                            },
                            {
                                path: ":amenityId/calendar",
                                component: () => import("@/pages/AmenityCalendar/AmenityCalendar.vue"),
                                name: "amenityCalendar",
                                meta: { requiresAuth: true }
                            },
                            {
                                path: ":amenityId/review",
                                component: () => import("@/pages/AmenityBookingReview/AmenityBookingReview.vue"),
                                name: "amenityBookingReview",
                                meta: { requiresAuth: true }
                            },
                            {
                                path: ":amenityId/confirmation",
                                component: () => import("@/pages/AmenityBookingConfirmation/AmenityBookingConfirmation.vue"),
                                name: "amenityBookingConfirmation",
                                meta: { requiresAuth: true }
                            }
                        ]
                    }
                ]
            },
            {
                path: "properties/:propertyId/promotions/:amenityId/overview/:promotionId",
                component: () => import("@/pages/PromotionOverview/PromotionOverview.vue"),
                name: "promotionOverview",
                meta: { requiresAuth: true }
            },
            {
                path: "properties/:propertyId/amenities/:amenityId/bookings/:bookingId",
                component: () => import("@/pages/BookingDeepLink/BookingDeepLink.vue"),
                name: "bookingDeepLink",
                meta: { requiresAuth: true }
            },
            {
                path: "properties/:propertyId/maintenance/tickets/new",
                component: () => import("@/pages/NewMaintenanceTicket/NewMaintenanceTicket.vue"),
                meta: { requiresAuth: true },
                name: "newMaintenanceTicket"
            },
            {
                path: "properties/:propertyId/maintenance/tickets/:ticketId",
                component: () => import("@/pages/MaintenanceTicketDetail/MaintenanceTicketDetail.vue"),
                meta: { requiresAuth: true },
                name: "maintenanceTicketDetail"
            }
        ]
    },
    {
        path: "/public/properties/:propertyId/directory",
        component: () => import("@/pages/BaseTenantDirectory/BaseTenantDirectory.vue"),
        meta: { requiresAuth: false },
        children: [
            {
                path: "tenants",
                component: () => import("@/pages/PropertyDirectoryTenants/PropertyDirectoryTenants.vue"),
                meta: { requiresAuth: false },
                name: "propertyDirectoryTenants"
            },
            {
                path: "tenants/:tenantId",
                component: () => import("@/pages/PropertyDirectoryTenantDetail/PropertyDirectoryTenantDetail.vue"),
                meta: { requiresAuth: false },
                name: "propertyDirectoryTenantDetail"
            },
            {
                path: "tenants/:tenantId/subtenants/:subTenantId",
                component: () => import("@/pages/PropertyDirectoryTenantDetail/PropertyDirectoryTenantDetail.vue"),
                meta: { requiresAuth: false },
                name: "propertyDirectorySubTenantDetail"
            },
            {
                path: "tenants/:tenantId/map",
                component: () => import("@/pages/PropertyDirectoryTenantMap/PropertyDirectoryTenantMap.vue"),
                meta: { requiresAuth: false },
                name: "propertyDirectoryTenantMap"
            },
            {
                path: "tenants/:tenantId/subtenants/:subTenantId/map",
                component: () => import("@/pages/PropertyDirectoryTenantMap/PropertyDirectoryTenantMap.vue"),
                meta: { requiresAuth: false },
                name: "propertyDirectorySubTenantMap"
            }
        ]
    },
    {
        path: "/public/guest-waiver/:hash",
        component: () => import("@/pages/GuestWaiverSigning/GuestWaiverSigning.vue"),
        meta: { requiresAuth: false },
        name: "guestWaiverSigning"
    },
    {
        path: "/public/guest-invitation/:code",
        component: () => import("@/pages/GuestInviteConfirmation/GuestInviteConfirmation.vue"),
        meta: { requiresAuth: false },
        name: "guestInviteConfirmation"
    },
    {
        path: "/public/loyalty/*",
        component: () => import("@/pages/LoyaltyLandingPage/LoyaltyLandingPage.vue"),
        meta: { requiresAuth: false },
        name: "loyaltyLandingPage"
    },
    {
        path: "/public/login",
        component: () => import("@/pages/Login/Login.vue"),
        meta: { requiresAuth: false },
        name: "login"
    }
]

const router = new VueRouter({
    mode: "history",
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.path !== from.path) {
            // Only scroll if the path has changed
            return savedPosition ? savedPosition : { x: 0, y: 0 }
        }
    }
})

export default router
